import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';
import './HomePage.css';
import logo from '../logo.PNG';

import FalconsSections from '../components/FalconsSections';

const HomePage = () => {
  const navigate = useNavigate();
  const [showPricing, setShowPricing] = useState(false);
  const scrollContainerRef = useRef(null);
  const spotlightRef = useRef(null);
  const logoRef = useRef(null);
  const leftBeamRef = useRef(null);
  const rightBeamRef = useRef(null);
  const diagonalLeftRef = useRef(null);
  const diagonalRightRef = useRef(null);
  const spotlight1Ref = useRef(null);
  const spotlight2Ref = useRef(null);

  useEffect(() => {
    // First spotlight sequence
    setTimeout(() => {
      spotlight1Ref.current?.classList.add('active');
    }, 200);

    setTimeout(() => {
      spotlight2Ref.current?.classList.add('active');
    }, 1000);

    // Original animation sequence with delayed timing
    setTimeout(() => {
      diagonalLeftRef.current?.classList.add('active');
    }, 2000);

    setTimeout(() => {
      diagonalRightRef.current?.classList.add('active');
    }, 2400);

    setTimeout(() => {
      leftBeamRef.current?.classList.add('active');
    }, 2800);

    setTimeout(() => {
      rightBeamRef.current?.classList.add('active');
    }, 3200);

    setTimeout(() => {
      spotlightRef.current?.classList.add('active');
    }, 3600);

    setTimeout(() => {
      logoRef.current?.classList.add('fully-illuminated');
      document.querySelector('.content')?.classList.add('content-animated');
    }, 4000);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px'
      }
    );

    const cards = document.querySelectorAll('.service-card');
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Check if user is logged in when component mounts
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    setShowPricing(isLoggedIn);
  }, []);

  useEffect(() => {
    // Add visibility class to pricing tiers after login
    if (showPricing) {
      const pricingElements = document.querySelectorAll('.pricing-tiers');
      pricingElements.forEach(el => {
        setTimeout(() => {
          el.classList.add('visible');
        }, 300);
      });
    }
  }, [showPricing]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('visible');
            }, entry.target.dataset.delay || 0);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px' // Consolidated rootMargin
      }
    );

    // Observe all intro and service cards with different delays
    const allCards = document.querySelectorAll('.intro-card, .service-card');
    allCards.forEach((card, index) => {
      card.dataset.delay = index * 200; // Stagger the animations
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const handleLearnMore = () => {
    window.location.href = '#about'; // Scroll to login form
  };

  const handleLogin = (e) => {
    e.preventDefault();
    localStorage.setItem('isLoggedIn', 'true');
    setShowPricing(true);
    window.location.href = '#services';
    window.location.reload(); // Add this line to ensure pricing updates
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    navigate('/auth', { state: { mode: 'reset' } });
  };

  const handleCreateAccount = (e) => {
    e.preventDefault();
    navigate('/auth', { state: { mode: 'signup' } });
  };

  const handleLogout = (e) => {
    e.preventDefault();
    localStorage.removeItem('isLoggedIn');
    setShowPricing(false);
    window.location.href = '#services';
    window.location.reload();
  };

  const handleBooking = (service, tier, price) => {
    if (!localStorage.getItem('isLoggedIn')) {
      handleLearnMore();
      return;
    }
    navigate('/booking', { state: { service, tier, price } });
  };

  const renderPricing = (prices, service) => {
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';

    if (!isLoggedIn) {
      return (
        <button className="learn-more-btn pulse-effect" onClick={handleLearnMore}>
          Learn More
        </button>
      );
    }

    return (
      <div className="pricing-tiers slide-in">
        <div className="price-tier" onClick={() => handleBooking(service, 'Basic', prices.basic)}>
          <h4>Basic</h4>
          <span className="price">{prices.basic}</span>
        </div>
        <div className="price-tier" onClick={() => handleBooking(service, 'Standard', prices.standard)}>
          <h4>Standard</h4>
          <span className="price">{prices.standard}</span>
        </div>
        <div className="price-tier" onClick={() => handleBooking(service, 'Premium', prices.premium)}>
          <h4>Premium</h4>
          <span className="price">{prices.premium}</span>
        </div>
      </div>
    );
  };

  const handleClick = (event) => {
    event.preventDefault();
    // Add your click handling logic here
  };

  const renderServiceCard = (service, description, prices) => (
    <div className="service-card">
      <img src={logo} alt={service} className="service-icon" />
      <h3>{service}</h3>
      <p>{description}</p>
      {renderPricing(prices, service)}
    </div>
  );

  return (
    <div className="home-container" ref={scrollContainerRef}>
      <section id="home" className="section">
        <div className="logo-spotlight-container">
          <div className="spotlight-beam spotlight1" ref={spotlight1Ref}></div>
          <div className="spotlight-beam spotlight2" ref={spotlight2Ref}></div>
          <div className="diagonal-beam diagonal-left" ref={diagonalLeftRef}></div>
          <div className="diagonal-beam diagonal-right" ref={diagonalRightRef}></div>
          <div className="corner-beam left-beam" ref={leftBeamRef}></div>
          <div className="corner-beam right-beam" ref={rightBeamRef}></div>
          <div className="spotlight" ref={spotlightRef}></div>

          <div className="logo-container" ref={logoRef}>
            <div className="flash"></div>
            <div className="circular-text">
              {Array.from("FALCONS  • DIGITAL SOLUTIONS • ").map((letter, i, arr) => (
                <span
                  key={i}
                  className="text-letter"
                  style={{
                    transform: `rotate(${i * (360 / arr.length)}deg)`
                  }}
                >
                  {letter}
                </span>
              ))}
            </div>
            <img src={logo} alt="Falcons Logo" className="logo" />
            <div className="logo-text">FALCONS</div>
            <div className="logo-subtitle">MARKETING Agency</div>
          </div>
        </div>
      </section>

      <FalconsSections />
    </div>
  );
};

export default HomePage;
